import React, { useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { apiClient, formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { Store, ArrowRight, HardHat, KeyRound, FileCheck2, Upload, ShieldCheck, Plus, X, Loader2 } from "lucide-react";
import { PasswordInput } from "../components/ui/password-input";
import { toast } from "sonner";
import { Link } from "react-router-dom";

export default function Login() {
  const { login, register, employeeLogin } = useAuth();
  const [audience, setAudience] = useState("owner"); // "owner" | "employee"
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // GST signup state
  const [gstFile, setGstFile] = useState(null);
  const [gstUploading, setGstUploading] = useState(false);
  const [gstVerification, setGstVerification] = useState(null); // full payload from /gst/verify
  const [gstError, setGstError] = useState("");
  const gstInputRef = useRef(null);

  // Mobile numbers state (up to 4)
  const [mobileNumbers, setMobileNumbers] = useState([""]);
  const [mobileDialogSeen, setMobileDialogSeen] = useState(false);
  const [mobileDialogOpen, setMobileDialogOpen] = useState(false);

  // Forgot-password state
  const [fpOpen, setFpOpen] = useState(false);
  const [fpStep, setFpStep] = useState("email"); // email | code | success
  const [fpEmail, setFpEmail] = useState("");
  const [fpCode, setFpCode] = useState("");
  const [fpPassword, setFpPassword] = useState("");
  const [fpDevCode, setFpDevCode] = useState("");
  const [fpDeliveredVia, setFpDeliveredVia] = useState(""); // "email" | "dev_response"
  const [fpBusy, setFpBusy] = useState(false);
  // #11 — When the entered email isn't registered, we replace the whole
  // dialog body with a "Sign up" CTA rather than sending a reset code.
  const [fpNotRegistered, setFpNotRegistered] = useState(false);

  const uploadGst = async (file) => {
    if (!file) return;
    setGstFile(file);
    setGstError("");
    setGstVerification(null);
    setGstUploading(true);
    try {
      // ---- Step 1: fast OCR — return the extracted GSTIN immediately.
      const fd = new FormData();
      fd.append("file", file);
      const ocrResp = await apiClient.post("/gst/ocr", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const ocr = ocrResp.data || {};
      if (!ocr.gstin) {
        throw new Error("Could not read a GSTIN from this certificate — please upload a clearer copy");
      }
      // Show the extracted GSTIN + a "verifying status..." pending state so the
      // user sees progress while Step 2 (Sandbox) runs. `verified:false` here
      // acts as our pending indicator until Step 2 resolves.
      setGstVerification({
        gstin: ocr.gstin,
        verified: false,
        pending: true,
        status: "Verifying...",
        legal_name: ocr.legal_name || "",
        trade_name: ocr.trade_name || "",
        provider: "",
      });

      // ---- Step 2: verify with Sandbox + duplicate check (separate call).
      const verifyResp = await apiClient.post("/gst/verify", {
        gstin: ocr.gstin,
        ocr_gstin: ocr.gstin,
        ocr_trade_name: ocr.trade_name || "",
        ocr_legal_name: ocr.legal_name || "",
      });
      const data = verifyResp.data;
      setGstVerification(data);
      // Auto-fill authoritative business name from GST records.
      if ((data.trade_name || data.legal_name) && !businessName.trim()) {
        setBusinessName((data.trade_name || data.legal_name).trim());
      }
      toast.success("GST Verified");
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message || "GST verification failed";
      setGstError(msg);
      setGstVerification(null);
      toast.error(msg);
    } finally {
      setGstUploading(false);
    }
  };

  const onMobileFocus = () => {
    // Spec §5 — one-time dialog on first mobile number entry.
    if (mode !== "register" || audience !== "owner") return;
    if (!mobileDialogSeen) {
      setMobileDialogOpen(true);
      setMobileDialogSeen(true);
    }
  };
  const updateMobile = (i, v) => setMobileNumbers((arr) => arr.map((m, idx) => (idx === i ? v : m)));
  const addMobile = () => {
    if (mobileNumbers.length >= 4) { toast.error("Maximum 4 mobile numbers"); return; }
    setMobileNumbers((arr) => [...arr, ""]);
  };
  const removeMobile = (i) => setMobileNumbers((arr) => arr.filter((_, idx) => idx !== i));

  const onSubmit = async (e) => {
    e.preventDefault();
    if ((password || "").length < 6) {
      toast.error("Password must be at least 6 characters.");
      return;
    }
    setSubmitting(true);
    let res;
    if (audience === "employee") {
      res = await employeeLogin(email.trim(), password);
    } else if (mode === "login") {
      res = await login(email.trim(), password);
    } else {
      // Register — GST + mobile validation.
      if (!gstVerification?.verified) {
        setSubmitting(false);
        toast.error("Please upload your GST certificate and wait for verification before signing up.");
        return;
      }
      const cleanNumbers = mobileNumbers.map((m) => (m || "").trim()).filter(Boolean);
      if (cleanNumbers.length === 0) {
        setSubmitting(false);
        toast.error("Add at least one mobile number");
        return;
      }
      const seen = new Set();
      for (const m of cleanNumbers) {
        const k = m.replace(/[^0-9+]/g, "");
        if (seen.has(k)) { setSubmitting(false); toast.error("Duplicate mobile number — please remove duplicates"); return; }
        seen.add(k);
      }
      res = await register({
        name: name.trim(),
        email: email.trim(),
        password,
        business_name: businessName.trim(),
        gstin: gstVerification.gstin,
        gst_verification: gstVerification,
        mobile_numbers: cleanNumbers,
      });
    }
    setSubmitting(false);
    if (!res.ok) {
      toast.error(res.error || "Failed");
    } else {
      toast.success(mode === "login" || audience === "employee" ? "Welcome back!" : "Account created");
      // Phase 7 #2 — resume a pending business-connection deep link after a
      // brand-new owner signs up (or logs in) from the /connect/:code page.
      if (audience !== "employee") {
        try {
          const pending = sessionStorage.getItem("konexo_pending_connect_code");
          if (pending) {
            sessionStorage.removeItem("konexo_pending_connect_code");
            window.location.href = `/connect/${pending}`;
          }
        } catch { /* ignore */ }
      }
    }
  };

  const requestReset = async () => {
    if (!fpEmail.trim() || !fpEmail.includes("@")) {
      toast.error("Enter a valid email");
      return;
    }
    setFpBusy(true);
    setFpNotRegistered(false);
    try {
      const { data } = await apiClient.post("/auth/password-reset/request", { email: fpEmail.trim() });
      // If Resend is configured, the backend returns delivered_via:"email" and NO code.
      // In dev/preview without a Resend key, delivered_via:"dev_response" with the code inline.
      setFpDevCode(data.code || "");
      setFpDeliveredVia(data.delivered_via || "email");
      setFpStep("code");
      if (data.delivered_via === "email") {
        toast.success("Check your inbox — we sent a 6-digit reset code");
      } else if (data.code) {
        toast.success("Reset code generated");
      } else {
        toast.success("If that email is registered, a reset code was sent");
      }
    } catch (err) {
      // #11 — Backend returns 404 with the exact "not registered" message
      // when no account matches. Swap the dialog UI to a Sign-Up CTA.
      const detail = formatApiErrorDetail(err.response?.data?.detail);
      if (err.response?.status === 404) {
        setFpNotRegistered(true);
      } else {
        toast.error(detail || "Could not start reset");
      }
    } finally {
      setFpBusy(false);
    }
  };

  const confirmReset = async () => {
    if (fpCode.trim().length < 6) { toast.error("Enter the 6-digit code"); return; }
    if (fpPassword.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setFpBusy(true);
    try {
      await apiClient.post("/auth/password-reset", {
        email: fpEmail.trim(),
        code: fpCode.trim(),
        new_password: fpPassword,
      });
      setFpStep("success");
      toast.success("Password reset — please log in with your new password.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err) || "Could not reset");
    } finally {
      setFpBusy(false);
    }
  };

  const closeReset = () => {
    setFpOpen(false);
    setFpStep("email"); setFpEmail(""); setFpCode(""); setFpPassword(""); setFpDevCode(""); setFpDeliveredVia("");
  };

  const Icon = audience === "employee" ? HardHat : Store;
  const iconBg = audience === "employee" ? "bg-indigo-600" : "bg-emerald-600";
  const iconShadow = audience === "employee" ? "shadow-indigo-600/30" : "shadow-emerald-600/30";

  return (
    <div className="min-h-screen px-4 py-10 flex flex-col">
      <div className="flex flex-col items-center gap-3 pt-6 pb-8">
        <div className={`w-16 h-16 rounded-2xl ${iconBg} text-white grid place-items-center shadow-lg ${iconShadow}`}>
          <Icon className="w-8 h-8" strokeWidth={2.5} />
        </div>
        <h1 className="text-3xl font-bold tracking-tight text-slate-900">KonexoCRM</h1>
        <p className="text-sm text-slate-500 text-center max-w-xs">
          {audience === "employee"
            ? "Apne tasks dekhein, status update karein."
            : "Business Connected — apne customers ko ek jagah par sambhalein."}
        </p>
      </div>

      <div className="flex bg-slate-100 rounded-xl p-1 mb-4">
        <button
          type="button"
          onClick={() => { setAudience("owner"); setMode("login"); }}
          data-testid="tab-audience-owner"
          className={`flex-1 h-11 rounded-lg text-sm font-semibold transition flex items-center justify-center gap-2 ${
            audience === "owner" ? "bg-white shadow text-slate-900" : "text-slate-500"
          }`}
        >
          <Store className="w-4 h-4" /> I&apos;m a Trader
        </button>
        <button
          type="button"
          onClick={() => setAudience("employee")}
          data-testid="tab-audience-employee"
          className={`flex-1 h-11 rounded-lg text-sm font-semibold transition flex items-center justify-center gap-2 ${
            audience === "employee" ? "bg-white shadow text-slate-900" : "text-slate-500"
          }`}
        >
          <HardHat className="w-4 h-4" /> I&apos;m Staff
        </button>
      </div>

      <form
        onSubmit={onSubmit}
        className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 space-y-4"
        data-testid="auth-form"
      >
        {audience === "owner" && (
          <div className="flex bg-slate-100 rounded-xl p-1">
            <button type="button" onClick={() => setMode("login")} data-testid="tab-login"
              className={`flex-1 h-10 rounded-lg text-sm font-semibold transition ${mode === "login" ? "bg-white shadow text-slate-900" : "text-slate-500"}`}>
              Login
            </button>
            <button type="button" onClick={() => setMode("register")} data-testid="tab-register"
              className={`flex-1 h-10 rounded-lg text-sm font-semibold transition ${mode === "register" ? "bg-white shadow text-slate-900" : "text-slate-500"}`}>
              Sign up
            </button>
          </div>
        )}

        {audience === "owner" && mode === "register" && (
          <>
            {/* --- GST certificate upload + verification (spec §1-4) --- */}
            <div className="space-y-2 border-2 border-dashed border-emerald-200 bg-emerald-50/40 rounded-2xl p-4" data-testid="gst-upload-block">
              <div className="flex items-center gap-2">
                <FileCheck2 className="w-4 h-4 text-emerald-700" />
                <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-800">Upload GST Registration Certificate</div>
              </div>
              <p className="text-xs text-slate-600">
                We read the GSTIN and verify it with the GST portal. 1 GSTIN = 1 KonexoCRM business.
              </p>

              <input
                ref={gstInputRef}
                type="file"
                accept="application/pdf,image/png,image/jpeg,image/webp"
                onChange={(e) => uploadGst(e.target.files?.[0])}
                className="hidden"
                data-testid="gst-file-input"
              />

              {!gstVerification && (
                <Button
                  type="button"
                  onClick={() => gstInputRef.current?.click()}
                  disabled={gstUploading}
                  data-testid="gst-upload-btn"
                  className="w-full h-12 bg-white border border-emerald-300 text-emerald-800 hover:bg-emerald-100 rounded-xl font-semibold gap-2"
                >
                  {gstUploading ? <><Loader2 className="w-4 h-4 animate-spin" /> Verifying…</> : <><Upload className="w-4 h-4" /> Choose GST certificate (PDF / image)</>}
                </Button>
              )}

              {gstFile && !gstVerification && !gstError && gstUploading && (
                <div className="text-xs text-slate-600 truncate">Reading <span className="font-mono">{gstFile.name}</span>…</div>
              )}

              {gstError && (
                <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-xl p-2" data-testid="gst-error">
                  {gstError}
                  <button type="button" onClick={() => { setGstError(""); setGstFile(null); gstInputRef.current?.click(); }} className="ml-2 font-semibold underline">
                    Try again
                  </button>
                </div>
              )}

              {gstVerification?.pending && (
                <div className="bg-white border border-amber-300 rounded-xl p-3 space-y-1" data-testid="gst-pending-panel">
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-3.5 h-3.5 animate-spin text-amber-600" />
                    <span className="text-[11px] font-bold uppercase tracking-wider text-amber-800">
                      GSTIN extracted, verifying status…
                    </span>
                  </div>
                  <div className="text-xs text-slate-700 pt-1">
                    <div><span className="text-slate-400">GSTIN</span> · <span className="font-mono font-semibold">{gstVerification.gstin}</span></div>
                    {gstVerification.trade_name && <div><span className="text-slate-400">Trade name (from certificate)</span> · {gstVerification.trade_name}</div>}
                  </div>
                </div>
              )}

              {gstVerification?.verified && (
                <div className="bg-white border border-emerald-300 rounded-xl p-3 space-y-1" data-testid="gst-verified-panel">
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center gap-1 h-6 px-2 rounded-full bg-emerald-100 text-emerald-800 text-[11px] font-bold">
                      <ShieldCheck className="w-3.5 h-3.5" /> GST Verified ✓
                    </span>
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                      via {gstVerification.provider}
                    </span>
                  </div>
                  <div className="text-xs text-slate-700 pt-1">
                    <div><span className="text-slate-400">GSTIN</span> · <span className="font-mono font-semibold">{gstVerification.gstin}</span></div>
                    {gstVerification.trade_name && <div><span className="text-slate-400">Trade name</span> · {gstVerification.trade_name}</div>}
                    {gstVerification.legal_name && <div><span className="text-slate-400">Legal name</span> · {gstVerification.legal_name}</div>}
                    {gstVerification.status && <div><span className="text-slate-400">Status</span> · {gstVerification.status}</div>}
                  </div>
                  <button
                    type="button"
                    onClick={() => { setGstVerification(null); setGstFile(null); gstInputRef.current?.click(); }}
                    data-testid="gst-replace"
                    className="text-[11px] font-semibold text-emerald-700 hover:underline"
                  >
                    Replace certificate
                  </button>
                </div>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="name">Your Name</Label>
              <Input id="name" data-testid="input-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Ramesh Kumar" required className="h-14 rounded-xl text-base" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="business">Shop / Business Name</Label>
              <Input id="business" data-testid="input-business" value={businessName} onChange={(e) => setBusinessName(e.target.value)} placeholder="Ramesh General Store" className="h-14 rounded-xl text-base" />
              {gstVerification?.trade_name && (
                <div className="text-[10px] text-emerald-700">Auto-filled from GST records — you can edit if you use a different display name.</div>
              )}
            </div>

            {/* --- Mobile numbers (spec §5) --- */}
            <div className="space-y-2" data-testid="mobile-numbers-block">
              <Label>Mobile numbers (up to 4)</Label>
              {mobileNumbers.map((m, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Input
                    value={m}
                    onChange={(e) => updateMobile(i, e.target.value)}
                    onFocus={i === 0 ? onMobileFocus : undefined}
                    placeholder={i === 0 ? "Primary mobile" : `Additional mobile ${i + 1}`}
                    type="tel"
                    data-testid={`input-mobile-${i}`}
                    className="h-12 rounded-xl text-base flex-1"
                    required={i === 0}
                  />
                  {mobileNumbers.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeMobile(i)}
                      data-testid={`remove-mobile-${i}`}
                      className="w-10 h-10 grid place-items-center rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-600"
                      aria-label="Remove mobile number"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
              {mobileNumbers.length < 4 && (
                <button
                  type="button"
                  onClick={addMobile}
                  data-testid="add-mobile-btn"
                  className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 hover:underline"
                >
                  <Plus className="w-3.5 h-3.5" /> Add another number
                </button>
              )}
            </div>
          </>
        )}

        <div className="space-y-1.5">
          <Label htmlFor="email">Email</Label>
          <Input id="email" data-testid="input-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@shop.com" required className="h-14 rounded-xl text-base" />
        </div>

        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label htmlFor="password">Password</Label>
            {mode === "login" && (
              <button
                type="button"
                onClick={() => { setFpEmail(email); setFpOpen(true); }}
                data-testid="forgot-password-link"
                className="text-xs font-semibold text-emerald-700 hover:underline"
              >
                Forgot password?
              </button>
            )}
          </div>
          <PasswordInput id="password" data-testid="input-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 6 characters" required minLength={6} className="h-14 rounded-xl text-base" />
        </div>

        <Button type="submit" disabled={submitting || (mode === "register" && audience === "owner" && !gstVerification?.verified)} data-testid="submit-auth"
          className={`w-full h-14 ${audience === "employee" ? "bg-indigo-600 hover:bg-indigo-700" : "bg-emerald-600 hover:bg-emerald-700"} text-white rounded-xl text-lg font-semibold gap-2 disabled:opacity-60`}>
          {submitting ? "Please wait..." : audience === "employee" ? "Login as Staff" : (mode === "login" ? "Login" : "Create Account")}
          <ArrowRight className="w-5 h-5" />
        </Button>

        {audience === "employee" && (
          <p className="text-xs text-center text-slate-500">
            Got an invite link? <Link to="/employee/accept" className="font-semibold text-indigo-700 underline">Open it here</Link>
          </p>
        )}
      </form>

      {/* Mobile-numbers onboarding dialog (spec §5) — shown once, on first mobile field focus */}
      <Dialog open={mobileDialogOpen} onOpenChange={setMobileDialogOpen}>
        <DialogContent className="max-w-sm rounded-3xl" data-testid="mobile-numbers-dialog">
          <DialogHeader>
            <DialogTitle>Add your team&apos;s mobile numbers</DialogTitle>
            <DialogDescription>
              Add all mobile numbers of the people who will be managing KonexoCRM for your business.
              You can add up to 4 numbers.
            </DialogDescription>
          </DialogHeader>
          <Button
            onClick={() => setMobileDialogOpen(false)}
            data-testid="mobile-dialog-ok"
            className="w-full h-12 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            Got it
          </Button>
        </DialogContent>
      </Dialog>

      {/* Forgot Password dialog (#1) */}
      <Dialog open={fpOpen} onOpenChange={(o) => { if (!o) closeReset(); }}>
        <DialogContent className="max-w-sm rounded-3xl" data-testid="forgot-password-dialog">
          <DialogHeader>
            <DialogTitle>Reset your password</DialogTitle>
            <DialogDescription>
              {fpStep === "email" && "Enter your registered email — we'll send a 6-digit reset code to your inbox."}
              {fpStep === "code" && (fpDeliveredVia === "email"
                ? `We just emailed the reset code to ${fpEmail}. Paste it below and choose a new password.`
                : "Enter the 6-digit reset code and choose a new password.")}
              {fpStep === "success" && "Your password has been reset."}
            </DialogDescription>
          </DialogHeader>

          {fpStep === "email" && !fpNotRegistered && (
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label>Email</Label>
                <Input value={fpEmail} onChange={(e) => setFpEmail(e.target.value)} placeholder="you@shop.com" type="email" data-testid="fp-email" className="h-12 rounded-xl" />
              </div>
              <Button onClick={requestReset} disabled={fpBusy} data-testid="fp-send-code" className="w-full h-12 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white">
                {fpBusy ? "Please wait..." : "Send reset code"}
              </Button>
            </div>
          )}

          {fpStep === "email" && fpNotRegistered && (
            <div className="space-y-3" data-testid="fp-not-registered">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-900">
                No account is registered with these details. Please sign up to create an account.
              </div>
              <Button
                onClick={() => {
                  setFpOpen(false);
                  setFpNotRegistered(false);
                  setMode("register");
                  setEmail(fpEmail);
                }}
                data-testid="fp-signup-cta"
                className="w-full h-12 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                Sign Up
              </Button>
              <button
                type="button"
                onClick={() => { setFpNotRegistered(false); setFpEmail(""); }}
                data-testid="fp-try-different"
                className="w-full text-xs font-semibold text-slate-500 hover:text-slate-700"
              >
                Try a different email
              </button>
            </div>
          )}

          {fpStep === "code" && (
            <div className="space-y-3">
              {fpDevCode && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-center gap-2 text-xs" data-testid="fp-dev-code-panel">
                  <KeyRound className="w-4 h-4 text-amber-600" />
                  <div className="flex-1">
                    <div className="text-amber-900 font-semibold">Dev-mode code (until email delivery is wired):</div>
                    <div className="text-lg font-bold tracking-widest text-amber-800 font-mono">{fpDevCode}</div>
                  </div>
                </div>
              )}
              <div className="space-y-1.5">
                <Label>6-digit code</Label>
                <Input value={fpCode} onChange={(e) => setFpCode(e.target.value)} placeholder="123456" data-testid="fp-code" inputMode="numeric" maxLength={6} autoComplete="one-time-code" name="one-time-code" className="h-12 rounded-xl tracking-widest text-center font-mono" />
              </div>
              <div className="space-y-1.5">
                <Label>New password</Label>
                <PasswordInput value={fpPassword} onChange={(e) => setFpPassword(e.target.value)} placeholder="At least 6 characters" data-testid="fp-new-password" autoComplete="new-password" className="h-12 rounded-xl" />
              </div>
              <Button onClick={confirmReset} disabled={fpBusy} data-testid="fp-confirm" className="w-full h-12 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white">
                {fpBusy ? "Please wait..." : "Reset password"}
              </Button>
            </div>
          )}

          {fpStep === "success" && (
            <div className="space-y-3 text-center">
              <div className="w-12 h-12 rounded-full bg-emerald-100 grid place-items-center mx-auto">
                <KeyRound className="w-6 h-6 text-emerald-700" />
              </div>
              <div className="text-sm text-slate-600">Your password has been reset. You can now log in with the new password.</div>
              <Button onClick={closeReset} data-testid="fp-close" className="w-full h-12 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white">
                Close
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
